import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { assert } from 'src/app/utils/assert.util';
import { AttributeMenuService } from '../../services/attribute-menu.service';
import { CheckoutService } from '../../services/checkout.service';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { PriceService } from 'src/app/services/price/price.service';
import { StoneCountsProviderService } from 'src/app/providers/stone-counts/stone-counts.provider.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { StoneTypesWithShapesService } from 'src/app/services/stone-types-with-shapes/stone-types-with-shapes.service';
import { StoneArrangementsWithSettingsService } from 'src/app/services/stone-arrangementsWithSettings/stone-arrangements-with-settings.service';

@Component({
  selector: 'app-retailer-summary',
  templateUrl: './retailer-summary.component.html',
  styleUrl: './retailer-summary.component.scss'
})
export class RetailerSummaryComponent {
  private loadingService = inject(LoadingService);
  private stoneTypesWithShapesService = inject(StoneTypesWithShapesService);
  private stoneArrangementsWithSettingsService = inject(StoneArrangementsWithSettingsService);
  private stoneCountsProvider = inject(StoneCountsProviderService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private configuredItemService = inject(ConfiguredItemService);
  private attributeMenuService = inject(AttributeMenuService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private checkoutService = inject(CheckoutService);
  private clipBoard = inject(Clipboard);
  private priceService = inject(PriceService);
  shouldShowCopyConfirmation = signal(false);
  isSharing = input(false);
  skuToShow = input<string | null>(null);
  poValue: string | null = null;
  didAgree = false;
  stoneCount = signal<number | null>(null);
  selectedAttributes = computed(() => this.selectedAttributeService.getValue());
  shouldShowEngraving = this.selectedAttributes().engravingType;
  retailPrice = computed(() => this.priceService.retailPrice());
  itemNumber = computed(() => this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributes()));
  attributesToShow = computed(() => Object.keys(this.selectedAttributes()).reduce((obj, key) => {
    if (key.includes('Material')) {
      if (key.includes('primary')) {
        obj['Edge Material'] = this.selectedAttributes().primaryMaterial?.displayName;
      } else {
        obj['Center Material'] = this.selectedAttributes().secondaryMaterial?.displayName;
      }
      return obj;
    }

    if (key.includes('gemstone')) {
      if (this.selectedAttributes().gemstoneType) {
        const arrangementWithSetting = this.stoneArrangementsWithSettingsService.values().find(aws => aws.stoneArrangementID === this.selectedAttributeService.configuredItem().gemstoneArrangement?.id && aws.stoneSettingID === this.selectedAttributeService.configuredItem().gemstoneSetting?.id);
        const typeWithShape = this.stoneTypesWithShapesService.values().find(tws => tws.stoneTypeID === this.selectedAttributeService.configuredItem().gemstoneType?.id && this.selectedAttributeService.configuredItem().gemstoneShape?.id === this.selectedAttributeService.configuredItem().gemstoneShape?.id);
        if (key.includes('Type')) {
          obj['Gemstone Type'] = typeWithShape?.displayName;
        } else if (key.includes('Carat')) {
          obj['Gemstone Carat'] = this.selectedAttributes().gemstoneSize?.displayName;
        } else {
          obj['Gemstone Arrangement'] = arrangementWithSetting?.displayName;
        }
      } else {
        obj['Gemstones'] = 'No Gemstones';
      }
      return obj;
    }

    if (key === 'engravingType') {
      const { engravingType, engravingText } = this.selectedAttributes();
      obj['Engraving'] = null;
      if (engravingType && engravingText) {
        obj['Engraving'] = engravingText;
      }
      return obj;
    }
    if (['engravingText'].includes(key)) {
      return obj;
    }
    if (key === 'size') {
      obj['Finger Size'] = this.selectedAttributes().size?.displayName;
      return obj;
    }

    if (key === 'form') {
      obj['Profile'] = this.selectedAttributes().form?.displayName;
      return obj;
    }

    if (key === 'width') {
      obj['Width'] = this.selectedAttributes().width?.displayName;
      return obj;
    }

    const keyArr = key.split('');
    keyArr[0] = keyArr[0].toUpperCase();
    const newKey = keyArr.join('');
    obj[newKey] = this.getSelectedAttributeValueForKey(key);
    return obj;
  }, {} as any));
  attributeKeys = computed(() => Object.keys(this.attributesToShow()));

  constructor() {
    effect(() => {
      this.poValue = this.selectedAttributeService.poNumber();
    });

    effect(async () => {
      if (this.selectedAttributes().gemstoneType) {
        this.loadingService.load('CheckoutSummary::DisplayedValue::StoneCounts');
        const stoneCount = await this.stoneCountsProvider.get(this.selectedAttributes());
        this.stoneCount.set(stoneCount);
        this.loadingService.done('CheckoutSummary::DisplayedValue::StoneCounts');
      } else {
        this.stoneCount.set(0);
      }
    }, { allowSignalWrites: true });
  }

  private getSelectedAttributeValueForKey(key: string) {
    const nameKeys = ['design', 'form'];
    const val = this.selectedAttributes()[key as keyof(ConfiguredItem)];
    if (nameKeys.includes(key)) {
      return (val as any)?.displayName ?? null;
    }
    return val;
  }

  saveForLater() {
    if (!this.isConfiguredItemComplete()) {
      this.attributeMenuService.shouldHilightRequired.set(true);
    } else {
      console.log('Save for later');
    }
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', 'retailer summary');
    }
  }

  isConfiguredItemComplete() {
    return Object.keys(this.selectedAttributeService.configuredItem()).filter(k => !k.includes('engraving') && !k.includes('design')).every(k => (this.selectedAttributeService.configuredItem() as any)[k]);
  }

  getAttributeValue(attribute: string) {
    return this.attributesToShow()[attribute];
  }

  engravingCheckChange() {
    if (!this.shouldShowEngraving) {
      this.selectedAttributeService.mutateEngravingType(null);
      this.selectedAttributeService.mutateEngravingText(null);
    }
  }

  shareClicked() {
    const url = new URL(window.location.href);
    const shareURL = new URL(`https://${url.host}/share`);

    const shareSKU = this.checkoutService.b2bData.haloStyle ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem());
    assert(shareSKU, 'No sku to copy');
    shareURL.searchParams.set('sku', shareSKU);

    const configuredItemSize = this.selectedAttributeService.configuredItem().size;
    assert(configuredItemSize, 'No size selected');
    const b2bSize = this.configuredItemService.getB2BFingerSizeObject(configuredItemSize);
    shareURL.searchParams.set('size', b2bSize.size.toString());
    shareURL.searchParams.set('isQuarterSize', b2bSize.isQuarterSize.toString());
    shareURL.searchParams.set('retailerID', encodeURIComponent(this.checkoutService.b2bData.retailerIframeID));

    const engravingText = this.selectedAttributeService.configuredItem().engravingText;
    const engravingType = this.selectedAttributeService.configuredItem().engravingType;
    if (engravingType && engravingText) {
      shareURL.searchParams.set('e', engravingType.shortName);
      shareURL.searchParams.set('et', engravingText);
    }

    this.clipBoard.copy(shareURL.href);

    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'share', 'share_link_copy', shareURL.href);
    }

    this.shouldShowCopyConfirmation.set(true);

    setTimeout(() => this.shouldShowCopyConfirmation.set(false), 2000);
  }

  print() {
    window.print();
  }
}
